<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import NextButton from 'dashboard/components-next/button/Button.vue';
import KanbanStageCascadeSelect from 'dashboard/components/widgets/KanbanStageCascadeSelect.vue';

const props = defineProps({
  kanbanId: {
    type: [Number, String],
    required: true,
  },
  stage: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const uiFlags = computed(() => getters['kanbans/getUIFlags'].value);
const labels = computed(() => getters['labels/getLabels'].value);
const agents = computed(() => getters['agents/getAgents'].value);
const automation = computed(() =>
  getters['kanbans/getStageAutomation'].value(props.stage.id)
);

const statusOptions = ['', 'open', 'pending', 'resolved'];

const emptyMessage = () => ({
  id: null,
  content: '',
  delayMinutes: 0,
  blobIds: [],
  attachments: [],
  isUploading: false,
});

// delay_minutes is stored in minutes; the UI lets the user pick whichever unit reads best
// ("in 3 days" beats "in 4320 minutes") and converts to/from minutes at the edges.
const UNIT_TO_MINUTES = { minutes: 1, hours: 60, days: 1440 };
const minutesToDelay = totalMinutes => {
  const minutes = Number(totalMinutes) || 0;
  if (minutes > 0 && minutes % UNIT_TO_MINUTES.days === 0) {
    return { value: minutes / UNIT_TO_MINUTES.days, unit: 'days' };
  }
  if (minutes > 0 && minutes % UNIT_TO_MINUTES.hours === 0) {
    return { value: minutes / UNIT_TO_MINUTES.hours, unit: 'hours' };
  }
  return { value: minutes, unit: 'minutes' };
};

const emptyTask = () => ({
  id: null,
  title: '',
  description: '',
  assigneeId: '',
  scheduleType: 'relative',
  delayValue: 0,
  delayUnit: 'minutes',
  daysOffset: 3,
  dueTime: '08:00',
});

const state = reactive({
  messages: [],
  tasks: [],
  labelList: [],
  conversationStatus: '',
  webhookEnabled: false,
  webhookUrl: '',
  moveToKanbanStageId: null,
});

const showWebhook = ref(false);

const syncStateFromAutomation = value => {
  const messages = value?.messages || [];
  state.messages = messages.length
    ? messages.map(message => ({
        id: message.id,
        content: message.content || '',
        delayMinutes: message.delay_minutes || 0,
        blobIds: (message.attachments || []).map(a => a.blob_id),
        attachments: (message.attachments || []).map(a => ({
          key: a.blob_id,
          filename: a.filename,
        })),
        isUploading: false,
      }))
    : [];
  const tasks = value?.tasks || [];
  state.tasks = tasks.length
    ? tasks.map(task => {
        const delay = minutesToDelay(task.delay_minutes);
        return {
          id: task.id,
          title: task.title || '',
          description: task.description || '',
          assigneeId: task.assignee_id || '',
          scheduleType: task.schedule_type || 'relative',
          delayValue: delay.value,
          delayUnit: delay.unit,
          daysOffset: task.days_offset || 3,
          dueTime: task.due_time || '08:00',
        };
      })
    : [];
  state.labelList = [...(value?.label_list || [])];
  state.conversationStatus = value?.conversation_status || '';
  state.webhookEnabled = value?.webhook_enabled || false;
  state.webhookUrl = value?.webhook_url || '';
  state.moveToKanbanStageId = value?.move_to_kanban_stage_id || null;
  showWebhook.value = state.webhookEnabled;
};

watch(automation, value => syncStateFromAutomation(value));

const addMessage = () => {
  state.messages.push(emptyMessage());
};
const removeMessage = index => {
  state.messages.splice(index, 1);
};

const addTask = () => {
  state.tasks.push(emptyTask());
};
const removeTask = index => {
  state.tasks.splice(index, 1);
};

const toggleLabel = title => {
  state.labelList = state.labelList.includes(title)
    ? state.labelList.filter(item => item !== title)
    : [...state.labelList, title];
};

const onFilesSelected = async (index, event) => {
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  if (!files.length) return;

  const message = state.messages[index];
  message.isUploading = true;
  try {
    await Promise.all(
      files.map(async file => {
        const { blobId } = await uploadFile(file);
        message.blobIds.push(blobId);
        message.attachments.push({ key: blobId, filename: file.name });
      })
    );
  } catch (error) {
    useAlert(t('KANBAN_SETTINGS.AUTOMATION.SEND_MESSAGE.UPLOAD_ERROR'));
  } finally {
    message.isUploading = false;
  }
};

const removeAttachment = (index, key) => {
  const message = state.messages[index];
  message.blobIds = message.blobIds.filter(id => id !== key);
  message.attachments = message.attachments.filter(a => a.key !== key);
};

const isMessageInvalid = message =>
  !message.content.trim() && !message.attachments.length;

const hasInvalidMessage = computed(() => state.messages.some(isMessageInvalid));

const isTaskInvalid = task =>
  !task.title.trim() ||
  (task.scheduleType === 'days_at_time' && !task.dueTime);

const hasInvalidTask = computed(() => state.tasks.some(isTaskInvalid));

const isWebhookInvalid = computed(
  () => state.webhookEnabled && !state.webhookUrl.trim()
);

const isFormInvalid = computed(
  () => hasInvalidMessage.value || hasInvalidTask.value || isWebhookInvalid.value
);

const onClose = () => emit('close');

const submit = async () => {
  if (isFormInvalid.value) return;

  try {
    await store.dispatch('kanbans/updateStageAutomation', {
      kanbanId: props.kanbanId,
      stageId: props.stage.id,
      messages_attributes: state.messages.map(message => ({
        id: message.id,
        content: message.content,
        delay_minutes: Number(message.delayMinutes) || 0,
        blob_ids: message.blobIds,
      })),
      tasks_attributes: state.tasks.map(task => ({
        id: task.id,
        title: task.title,
        description: task.description,
        assignee_id: task.assigneeId || null,
        schedule_type: task.scheduleType,
        delay_minutes:
          task.scheduleType === 'relative'
            ? Math.round(
                (Number(task.delayValue) || 0) *
                  UNIT_TO_MINUTES[task.delayUnit]
              )
            : 0,
        days_offset:
          task.scheduleType === 'days_at_time'
            ? Number(task.daysOffset) || 0
            : 0,
        due_time: task.scheduleType === 'days_at_time' ? task.dueTime : null,
      })),
      label_list: state.labelList,
      conversation_status: state.conversationStatus || null,
      webhook_enabled: state.webhookEnabled,
      webhook_url: state.webhookEnabled ? state.webhookUrl : '',
      move_to_kanban_stage_id: state.moveToKanbanStageId,
    });
    useAlert(t('KANBAN_SETTINGS.AUTOMATION.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    useAlert(
      error.message || t('KANBAN_SETTINGS.AUTOMATION.API.ERROR_MESSAGE')
    );
  }
};

onMounted(async () => {
  store.dispatch('labels/get');
  if (!agents.value.length) store.dispatch('agents/get');
  if (automation.value) {
    syncStateFromAutomation(automation.value);
    return;
  }
  try {
    const data = await store.dispatch('kanbans/getStageAutomation', {
      kanbanId: props.kanbanId,
      stageId: props.stage.id,
    });
    syncStateFromAutomation(data);
  } catch (error) {
    syncStateFromAutomation(null);
  }
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="t('KANBAN_SETTINGS.AUTOMATION.TITLE')"
      :header-content="
        t('KANBAN_SETTINGS.AUTOMATION.DESC', { stage: stage.name })
      "
    />
    <form class="flex flex-col w-full gap-4 px-4 pb-4" @submit.prevent="submit">
      <section>
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('KANBAN_SETTINGS.AUTOMATION.SEND_MESSAGE.LABEL') }}
          </label>
          <NextButton
            type="button"
            faded
            sm
            icon="i-lucide-plus"
            :label="t('KANBAN_SETTINGS.AUTOMATION.SEND_MESSAGE.ADD')"
            @click="addMessage"
          />
        </div>

        <div
          v-for="(message, index) in state.messages"
          :key="index"
          class="flex flex-col gap-2 p-3 mt-2 border rounded-lg border-n-weak"
        >
          <div class="flex items-start gap-2">
            <textarea
              v-model="message.content"
              :rows="2"
              class="flex-1"
              :placeholder="
                t('KANBAN_SETTINGS.AUTOMATION.SEND_MESSAGE.PLACEHOLDER')
              "
              :class="{ error: isMessageInvalid(message) }"
            />
            <NextButton
              type="button"
              icon="i-lucide-trash"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="removeMessage(index)"
            />
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <span
              v-for="attachment in message.attachments"
              :key="attachment.key"
              class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              <i class="i-lucide-paperclip size-3" />
              <span class="max-w-[8rem] truncate">{{
                attachment.filename
              }}</span>
              <button
                type="button"
                class="i-lucide-x size-3 hover:text-n-ruby-11"
                @click="removeAttachment(index, attachment.key)"
              />
            </span>

            <label
              class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full cursor-pointer border border-dashed border-n-weak text-n-slate-10 hover:bg-n-alpha-1"
            >
              <i
                :class="
                  message.isUploading
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-image-plus'
                "
                class="size-3"
              />
              {{ t('KANBAN_SETTINGS.AUTOMATION.SEND_MESSAGE.ATTACH') }}
              <input
                type="file"
                multiple
                class="hidden"
                :disabled="message.isUploading"
                @change="onFilesSelected(index, $event)"
              />
            </label>

            <div class="flex items-center gap-1.5 ml-auto">
              <span class="text-xs text-n-slate-10">
                {{ t('KANBAN_SETTINGS.AUTOMATION.DELAY.LABEL') }}
              </span>
              <input
                v-model.number="message.delayMinutes"
                type="number"
                min="0"
                class="w-16 text-xs"
              />
              <span class="text-xs text-n-slate-10">
                {{ t('KANBAN_SETTINGS.AUTOMATION.DELAY.MINUTES_SUFFIX') }}
              </span>
            </div>
          </div>
        </div>

        <p v-if="!state.messages.length" class="mt-2 text-xs text-n-slate-10">
          {{ t('KANBAN_SETTINGS.AUTOMATION.SEND_MESSAGE.EMPTY') }}
        </p>
      </section>

      <section>
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.LABEL') }}
          </label>
          <NextButton
            type="button"
            faded
            sm
            icon="i-lucide-plus"
            :label="t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.ADD')"
            @click="addTask"
          />
        </div>

        <div
          v-for="(task, index) in state.tasks"
          :key="index"
          class="flex flex-col gap-2 p-3 mt-2 border rounded-lg border-n-weak"
        >
          <div class="flex items-start gap-2">
            <input
              v-model="task.title"
              type="text"
              class="flex-1"
              :placeholder="
                t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.TITLE_PLACEHOLDER')
              "
              :class="{ error: isTaskInvalid(task) }"
            />
            <NextButton
              type="button"
              icon="i-lucide-trash"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="removeTask(index)"
            />
          </div>

          <textarea
            v-model="task.description"
            :rows="2"
            :placeholder="
              t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.DESCRIPTION_PLACEHOLDER')
            "
          />

          <select v-model="task.assigneeId" class="text-xs">
            <option value="">
              {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.ASSIGNEE_UNSET') }}
            </option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>

          <div class="flex items-center gap-3 p-2 rounded-md bg-n-alpha-1">
            <label class="flex items-center gap-1.5 text-xs text-n-slate-11">
              <input
                v-model="task.scheduleType"
                type="radio"
                value="relative"
                :name="`schedule-type-${index}`"
              />
              {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.SCHEDULE_TYPE.RELATIVE') }}
            </label>
            <label class="flex items-center gap-1.5 text-xs text-n-slate-11">
              <input
                v-model="task.scheduleType"
                type="radio"
                value="days_at_time"
                :name="`schedule-type-${index}`"
              />
              {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.SCHEDULE_TYPE.DAYS_AT_TIME') }}
            </label>
          </div>

          <div v-if="task.scheduleType === 'relative'" class="flex items-center gap-1.5">
            <span class="text-xs text-n-slate-10">
              {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.DUE_IN') }}
            </span>
            <input
              v-model.number="task.delayValue"
              type="number"
              min="0"
              class="w-16 text-xs"
            />
            <select v-model="task.delayUnit" class="text-xs">
              <option value="minutes">
                {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.DELAY_UNIT.MINUTES') }}
              </option>
              <option value="hours">
                {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.DELAY_UNIT.HOURS') }}
              </option>
              <option value="days">
                {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.DELAY_UNIT.DAYS') }}
              </option>
            </select>
          </div>
          <div v-else class="flex items-center gap-1.5">
            <span class="text-xs text-n-slate-10">
              {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.DUE_IN_DAYS') }}
            </span>
            <input
              v-model.number="task.daysOffset"
              type="number"
              min="0"
              class="w-16 text-xs"
            />
            <span class="text-xs text-n-slate-10">
              {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.AT_TIME') }}
            </span>
            <input
              v-model="task.dueTime"
              type="time"
              class="text-xs"
              :class="{ error: task.scheduleType === 'days_at_time' && !task.dueTime }"
            />
          </div>
        </div>

        <p v-if="!state.tasks.length" class="mt-2 text-xs text-n-slate-10">
          {{ t('KANBAN_SETTINGS.AUTOMATION.CREATE_TASK.EMPTY') }}
        </p>
      </section>

      <section>
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN_SETTINGS.AUTOMATION.LABELS.LABEL') }}
        </label>
        <div
          class="flex flex-col mt-2 border rounded-lg border-n-weak divide-y divide-n-weak max-h-40 overflow-y-auto"
        >
          <label
            v-for="label in labels"
            :key="label.id"
            class="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-n-alpha-1"
          >
            <input
              type="checkbox"
              class="checkbox"
              :checked="state.labelList.includes(label.title)"
              @change="toggleLabel(label.title)"
            />
            <span
              class="rounded-full size-2.5 shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <span class="text-sm text-n-slate-12">{{ label.title }}</span>
          </label>
          <p v-if="!labels.length" class="px-3 py-2 text-xs text-n-slate-10">
            {{ t('KANBAN_SETTINGS.AUTOMATION.LABELS.EMPTY') }}
          </p>
        </div>
      </section>

      <section>
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN_SETTINGS.AUTOMATION.STATUS.LABEL') }}
        </label>
        <select v-model="state.conversationStatus" class="mt-1">
          <option v-for="option in statusOptions" :key="option" :value="option">
            {{
              option
                ? t(
                    `KANBAN_SETTINGS.AUTOMATION.STATUS.OPTIONS.${option.toUpperCase()}`
                  )
                : t('KANBAN_SETTINGS.AUTOMATION.STATUS.OPTIONS.NONE')
            }}
          </option>
        </select>
      </section>

      <section>
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN_SETTINGS.AUTOMATION.MOVE_TO_KANBAN.LABEL') }}
        </label>
        <p class="mt-0.5 text-xs text-n-slate-10">
          {{ t('KANBAN_SETTINGS.AUTOMATION.MOVE_TO_KANBAN.DESC') }}
        </p>
        <KanbanStageCascadeSelect
          v-model="state.moveToKanbanStageId"
          :exclude-stage-id="stage.id"
          class="mt-2"
        />
      </section>

      <section>
        <button
          type="button"
          class="flex items-center gap-1 text-xs font-medium text-n-slate-10 hover:text-n-slate-11"
          @click="showWebhook = !showWebhook"
        >
          <i class="i-lucide-webhook size-3.5" />
          {{ t('KANBAN_SETTINGS.AUTOMATION.WEBHOOK.TOGGLE_LABEL') }}
          <i
            :class="
              showWebhook ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
            class="size-3.5"
          />
        </button>
        <div
          v-if="showWebhook"
          class="flex flex-col gap-2 p-3 mt-2 border border-dashed rounded-lg border-n-weak"
        >
          <label
            class="flex items-center gap-2 text-xs font-medium text-n-slate-11"
          >
            <input
              v-model="state.webhookEnabled"
              type="checkbox"
              class="checkbox"
            />
            {{ t('KANBAN_SETTINGS.AUTOMATION.WEBHOOK.ENABLE') }}
          </label>
          <input
            v-if="state.webhookEnabled"
            v-model="state.webhookUrl"
            type="url"
            class="text-sm"
            :class="{ error: isWebhookInvalid }"
            :placeholder="
              t('KANBAN_SETTINGS.AUTOMATION.WEBHOOK.URL_PLACEHOLDER')
            "
          />
          <p class="text-[11px] text-n-slate-9">
            {{ t('KANBAN_SETTINGS.AUTOMATION.WEBHOOK.HELP') }}
          </p>
        </div>
      </section>

      <div class="flex items-center justify-end w-full gap-2 pt-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="t('KANBAN_SETTINGS.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="t('KANBAN_SETTINGS.AUTOMATION.SAVE')"
          :disabled="isFormInvalid"
          :is-loading="uiFlags.isUpdatingAutomation"
        />
      </div>
    </form>
  </div>
</template>
